import { useEffect, useRef, useState } from 'react';

import type { Message } from '@/api/types';
import { useT } from '@/i18n';
import { useAutoHide } from '@/hooks/useAutoHide';
import { useMessageContent } from '@/hooks/useMessageContent';
import { copyText } from '@/lib/clipboard';
import { formatBytes } from '@/lib/format';
import { MASK_TEXT } from '@/lib/secretMask';
import { splitPassword } from '@/lib/secretKind';
import styles from './SecretView.module.css';

import { Button, IconButton } from './Button';
import { DecryptText } from './DecryptText';
import { CheckIcon, CopyIcon, DownloadIcon, EyeIcon, EyeOffIcon, FileIcon, TrashIcon } from './Icons';
import { PasswordPlate, PlateButton } from './PasswordPlate';
import { useToast } from './Toast';
import { percent, useFileDownload, type FetchChunk } from './useFileDownload';

interface SecretItemProps {
  readonly message: Message;
  readonly bridgeId: string;
  readonly bridgeKey: CryptoKey;
  readonly fetchChunk: FetchChunk;
  /** The only item on the bridge: on a phone its own delete button gives way to “Delete bridge”, which does the same. */
  readonly sole?: boolean;
  readonly canDelete: boolean;
  readonly onDelete: (id: string) => void;
  /** The person pressed Open secret before the bridge opened: show the text at once. */
  readonly startRevealed?: boolean;
  /** The person pressed Download before the bridge opened: start the download as soon as the file can be read. */
  readonly autoDownload?: boolean;
}

/**
 * One shared item on a read-only bridge. Text is decrypted in front of the
 * viewer with the scramble effect, then can be hidden, shown again and
 * copied; files download on demand.
 */
export function SecretItem({ message, bridgeId, bridgeKey, fetchChunk, sole = false, canDelete, onDelete, startRevealed = false, autoDownload = false }: SecretItemProps): React.JSX.Element {
  const t = useT();
  const toast = useToast();
  const content = useMessageContent(message, bridgeKey, bridgeId);
  // shown as dots at first, as a password field would; the eye reveals it
  const [hidden, setHidden] = useState(!startRevealed);
  const [copied, setCopied] = useState(false);
  // a shown secret goes back to dots when the page is hidden and after a minute
  useAutoHide(content.kind === 'text' && !hidden, () => {
    setHidden(true);
  });
  const file = useFileDownload(message.kind === 'file' ? message : null, content.kind === 'file' ? content.header : null, bridgeKey, fetchChunk);

  // the person pressed Download on the sealed card: it starts once, when the file's header has been read
  const started = useRef(false);
  const ready = content.kind === 'file';
  useEffect(() => {
    if (!autoDownload || !ready || started.current) return;
    started.current = true;
    void file.download();
  }, [autoDownload, ready, file]);

  // a password (and the note after it) goes on a plate; any other text stays text
  const password = content.kind === 'text' ? splitPassword(content.text) : null;

  const copy = async (): Promise<void> => {
    if (content.kind !== 'text') return;
    if (await copyText(password === null ? content.text : password.password)) {
      // the button itself says “Copied”, right where the person looks
      setCopied(true);
      window.setTimeout(() => {
        setCopied(false);
      }, 1800);
    } else {
      toast.error(t('secretView.copyFailed'));
    }
  };

  return (
    <article className={[styles.secretItem, password !== null ? styles.secretItemPassword : '', content.kind === 'text' && password === null ? styles.secretItemText : ''].join(' ')} data-testid="message" aria-label={message.kind === 'text' ? t('secretView.sharedText') : t('secretView.sharedFile')}>
      {content.kind === 'pending' ? <p className={styles.secretPending}>{t('secretView.decrypting')}</p> : null}
      {content.kind === 'error' ? <p className={[styles.secretPending, styles.secretError].join(' ')}>{content.reason === 'version' ? t('secretView.errorVersion') : t('secretView.errorKey')}</p> : null}
      {content.kind === 'text' && password !== null ? (
        <>
          <PasswordPlate
            password={password.password}
            shown={!hidden}
            onToggle={() => {
              setHidden((h) => !h);
            }}
            testId="message-text"
          >
            <PlateButton label={copied ? t('common.copied') : t('secretView.copyPassword')} onClick={() => void copy()} testId="copy-secret">
              {copied ? <CheckIcon size={16} /> : <CopyIcon size={16} />}
            </PlateButton>
            {canDelete ? (
              <PlateButton label={t('secretView.deleteMessage')} className={sole ? styles.msgDelete : undefined} onClick={() => { onDelete(message.id); }}>
                <TrashIcon size={16} />
              </PlateButton>
            ) : null}
          </PasswordPlate>
          {password.note === '' ? null : (
            <p className={styles.secretNote} data-testid="password-note">
              {password.note}
            </p>
          )}
        </>
      ) : null}
      {content.kind === 'text' && password === null ? (
        <>
          <pre className={styles.secretText} data-testid="message-text">
            {hidden ? MASK_TEXT : <DecryptText text={content.text} />}
          </pre>
          <div className={styles.secretActions}>
            <Button type="button" size="small" className={styles.copyBtn} icon={copied ? <CheckIcon size={16} /> : <CopyIcon size={16} />} onClick={() => void copy()} data-testid="copy-secret" aria-label={copied ? t('common.copied') : t('common.copy')}>
              <span className={styles.copyLabel}>{copied ? t('common.copied') : t('common.copy')}</span>
            </Button>
            <IconButton label={hidden ? t('secretView.showSecret') : t('secretView.hideSecret')} aria-pressed={!hidden} onClick={() => { setHidden((h) => !h); }}>
              {hidden ? <EyeIcon size={16} /> : <EyeOffIcon size={16} />}
            </IconButton>
            {canDelete ? (
              <IconButton label={t('secretView.deleteMessage')} className={sole ? styles.msgDelete : undefined} onClick={() => { onDelete(message.id); }}>
                <TrashIcon size={16} />
              </IconButton>
            ) : null}
          </div>
        </>
      ) : null}
      {content.kind === 'file' ? (
        <div className={styles.secretFile}>
          <FileIcon size={26} />
          <div className={styles.secretFileName}>
            <div>{content.header.name}</div>
            <small>{formatBytes(content.header.size)}</small>
            {autoDownload ? (
              <small data-testid="download-hint">{t('secretView.downloadHint')}</small>
            ) : null}
          </div>
          <Button type="button" size="small" icon={<DownloadIcon size={15} />} onClick={() => void file.download()} disabled={file.progress !== null} aria-label={t('secretView.downloadFile')}>
            {file.progress === null ? t('common.download') : t('secretView.decryptingPercent', { percent: percent(file.progress) })}
          </Button>
          {canDelete ? (
            <IconButton label={t('secretView.deleteMessage')} onClick={() => { onDelete(message.id); }}>
              <TrashIcon size={16} />
            </IconButton>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
